/**
 * API endpoints para Suppliers (Proveedores)
 */

import { DELETE, GET, POST, PUT, PATCH } from "../config";

/**
 * Listar proveedores
 */
export const list = async (page = 1, filters = {}) => {
  const params = new URLSearchParams();
  // Asegurar que page sea un número
  params.append("page", typeof page === "number" ? page : 1);

  // Permitir page_size en filters, por defecto 100 para selects
  const pageSize = filters.page_size || 100;
  params.append("page_size", pageSize);

  // Agregar filtros
  if (filters.name) params.append("name__icontains", filters.name);
  if (filters.branch) params.append("branch", filters.branch);
  if (filters.tax_id) params.append("tax_id__icontains", filters.tax_id);
  if (filters.address) params.append("address__icontains", filters.address);
  if (filters.status) params.append("status", filters.status);

  const response = await GET(`suppliers/suppliers/?${params.toString()}`);
  return response.data;
};

/**
 * Obtener detalle de proveedor
 */
export const getDetail = async (id) => {
  const response = await GET(`suppliers/suppliers/${id}/`);
  return response.data;
};

/**
 * Crear proveedor
 */
export const create = async (data) => {
  const response = await POST("suppliers/suppliers/", data);
  return response.data;
};

/**
 * Actualizar proveedor
 */
export const update = async (id, data) => {
  const response = await PUT(`suppliers/suppliers/${id}/`, data);
  return response.data;
};

/**
 * Eliminar proveedor
 */
export const remove = async (id) => {
  const response = await DELETE(`suppliers/suppliers/${id}/`);
  return response.data;
};

/**
 * Obtener estadísticas de proveedores
 */
export const get_stats = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.branch) params.append("branch", filters.branch);
  if (filters.name) params.append("name", filters.name);
  if (filters.tax_id) params.append("tax_id", filters.tax_id);

  const response = await GET(`suppliers/suppliers/stats/?${params.toString()}`);
  return response.data;
};

/**
 * Listar productos de un proveedor
 */
export const getProducts = async (supplierId, params = {}) => {
  const response = await GET(`suppliers/suppliers/${supplierId}/products/`, { params });
  return response.data;
};

/**
 * =============================================
 * ENDPOINTS PARA PRODUCTOS DE PROVEEDORES
 * =============================================
 */

/**
 * Listar productos de proveedor
 */
export const listSupplierProducts = async (params = {}) => {
  const urlParams = new URLSearchParams();

  if (params.supplier) urlParams.append("supplier", params.supplier);
  if (params.branch) urlParams.append("branch", params.branch);
  if (params.page_size) urlParams.append("page_size", params.page_size);
  if (params.page) urlParams.append("page", params.page);
  if (params.search) urlParams.append("search", params.search);

  const url = urlParams.toString()
    ? `suppliers/supplier-products/?${urlParams.toString()}`
    : "suppliers/supplier-products/";

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener detalle de producto de proveedor
 */
export const getSupplierProductDetail = async (id) => {
  const response = await GET(`suppliers/supplier-products/${id}/`);
  return response.data;
};

/**
 * Crear producto de proveedor
 */
export const createSupplierProduct = async (data) => {
  const response = await POST("suppliers/supplier-products/", data);
  return response.data;
};

/**
 * Actualizar producto de proveedor (PUT - actualización completa)
 */
export const updateSupplierProduct = async (id, data) => {
  const response = await PUT(`suppliers/supplier-products/${id}/`, data);
  return response.data;
};

/**
 * Actualizar parcialmente producto de proveedor (PATCH - actualización parcial)
 */
export const patchSupplierProduct = async (id, data) => {
  const response = await PATCH(`suppliers/supplier-products/${id}/`, data);
  return response.data;
};

/**
 * Eliminar producto de proveedor
 */
export const removeSupplierProduct = async (id) => {
  const response = await DELETE(`suppliers/supplier-products/${id}/`);
  return response.data;
};

/**
 * =============================================
 * ENDPOINTS PARA ÓRDENES DE COMPRA
 * =============================================
 */

/**
 * Listar órdenes de compra
 */
export const listPurchaseOrders = async (params = {}) => {
  const urlParams = new URLSearchParams();

  if (params.page) urlParams.append("page", params.page);
  if (params.page_size) urlParams.append("page_size", params.page_size);
  if (params.branch) urlParams.append("branch", params.branch);
  if (params.status) urlParams.append("status", params.status);
  if (params.payment_status)
    urlParams.append("payment_status", params.payment_status);
  if (params.supplier) urlParams.append("supplier", params.supplier);
  if (params.start_date) urlParams.append("start_date", params.start_date);
  if (params.end_date) urlParams.append("end_date", params.end_date);

  const response = await GET(
    `suppliers/purchase-orders/?${urlParams.toString()}`
  );
  return response.data;
};

/**
 * Obtener detalle de orden de compra (incluye items y pagos)
 */
export const getPurchaseOrder = async (id) => {
  const response = await GET(`suppliers/purchase-orders/${id}/`);
  return response.data;
};

/**
 * Crear orden de compra
 */
export const createPurchaseOrder = async (data) => {
  const response = await POST("suppliers/purchase-orders/", data);
  return response.data;
};

/**
 * Actualizar orden de compra
 */
export const updatePurchaseOrder = async (id, data) => {
  const response = await PUT(`suppliers/purchase-orders/${id}/`, data);
  return response.data;
};

/**
 * Eliminar orden de compra
 */
export const removePurchaseOrder = async (id) => {
  const response = await DELETE(`suppliers/purchase-orders/${id}/`);
  return response.data;
};

/**
 * Registrar pago para una orden de compra
 */
export const addPurchaseOrderPayment = async (orderId, paymentData) => {
  const response = await POST(`suppliers/purchase-orders/${orderId}/add_payment/`, paymentData);
  return response.data;
};

/**
 * Obtener pagos de una orden de compra
 */
export const getPurchaseOrderPayments = async (orderId) => {
  const response = await GET(`suppliers/purchase-orders/${orderId}/payments/`);
  return response.data;
};

/**
 * Marcar orden de compra como recibida
 */
export const markPurchaseOrderAsReceived = async (orderId, data = {}) => {
  const response = await POST(`suppliers/purchase-orders/${orderId}/mark_received/`, data);
  return response.data;
};

/**
 * Marcar orden de compra como parcialmente recibida
 */
export const markPurchaseOrderAsPartiallyReceived = async (orderId, data = {}) => {
  const response = await POST(
    `suppliers/purchase-orders/${orderId}/mark_partial_received/`,
    data
  );
  return response.data;
};

/**
 * Cancelar orden de compra
 */
export const cancelPurchaseOrder = async (orderId, data = {}) => {
  const response = await POST(`suppliers/purchase-orders/${orderId}/cancel/`, data);
  return response.data;
};

/**
 * =============================================
 * ENDPOINTS PARA ITEMS DE ÓRDENES DE COMPRA
 * =============================================
 */

/**
 * Listar items de órdenes de compra
 */
export const listPurchaseOrderItems = async (params = {}) => {
  const response = await GET("suppliers/purchase-order-items/", { params });
  return response.data;
};

/**
 * Obtener detalle de item de orden de compra
 */
export const getPurchaseOrderItem = async (id) => {
  const response = await GET(`suppliers/purchase-order-items/${id}/`);
  return response.data;
};

/**
 * Crear item de orden de compra
 */
export const createPurchaseOrderItem = async (data) => {
  const response = await POST("suppliers/purchase-order-items/", data);
  return response.data;
};

/**
 * Actualizar item de orden de compra
 */
export const updatePurchaseOrderItem = async (id, data) => {
  const response = await PUT(`suppliers/purchase-order-items/${id}/`, data);
  return response.data;
};

/**
 * Registrar cantidad recibida de un item
 */
export const receivePurchaseOrderItem = async (itemId, data = {}) => {
  const response = await POST(`suppliers/purchase-order-items/${itemId}/receive/`, data);
  return response.data;
};

/**
 * =============================================
 * ENDPOINTS PARA PAGOS DE ÓRDENES DE COMPRA
 * =============================================
 */

/**
 * Listar pagos de órdenes de compra
 */
export const listPurchaseOrderPayments = async (params = {}) => {
  const response = await GET("suppliers/purchase-order-payments/", { params });
  return response.data;
};

/**
 * Obtener detalle de pago de orden de compra
 */
export const getPurchaseOrderPayment = async (id) => {
  const response = await GET(`suppliers/purchase-order-payments/${id}/`);
  return response.data;
};

/**
 * Crear pago de orden de compra
 */
export const createPurchaseOrderPayment = async (data) => {
  const response = await POST("suppliers/purchase-order-payments/", data);
  return response.data;
};

/**
 * Actualizar pago de orden de compra
 */
export const updatePurchaseOrderPayment = async (id, data) => {
  const response = await PUT(`suppliers/purchase-order-payments/${id}/`, data);
  return response.data;
};

const suppliersApi = {
  list,
  getDetail,
  create,
  update,
  remove,
  get_stats,
  getProducts,
  listSupplierProducts, // Agregado para acceso directo
  supplierProducts: {
    list: listSupplierProducts,
    getDetail: getSupplierProductDetail,
    create: createSupplierProduct,
    update: updateSupplierProduct,
    patch: patchSupplierProduct,
    remove: removeSupplierProduct,
  },
  purchaseOrders: {
    list: listPurchaseOrders,
    get: getPurchaseOrder,
    create: createPurchaseOrder,
    update: updatePurchaseOrder,
    remove: removePurchaseOrder,
    addPayment: addPurchaseOrderPayment,
    getPayments: getPurchaseOrderPayments,
    markAsReceived: markPurchaseOrderAsReceived,
    markAsPartiallyReceived: markPurchaseOrderAsPartiallyReceived,
    cancel: cancelPurchaseOrder,
  },
  purchaseOrderItems: {
    list: listPurchaseOrderItems,
    get: getPurchaseOrderItem,
    create: createPurchaseOrderItem,
    update: updatePurchaseOrderItem,
    receive: receivePurchaseOrderItem,
  },
  purchaseOrderPayments: {
    list: listPurchaseOrderPayments,
    get: getPurchaseOrderPayment,
    create: createPurchaseOrderPayment,
    update: updatePurchaseOrderPayment,
  },
};

export default suppliersApi;
